import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery, queryDelete, queryGet, queryPost, queryPut } from '@/utils';

export const parentApi = createApi({
  reducerPath: 'parentApi',
  baseQuery,
  endpoints: (builder) => ({
    searchParents: builder.mutation({
      query: (body) => queryPost('/search/parents', body),
    }),
    quickSearchParent: builder.mutation({
      query: (body) => queryPost('/search/parents/quick', body),
    }),
    getParentSearchResults: builder.query({
      query: ({ searchId, pageNumber = 1, pageSize = 10 }) =>
        queryGet(`/search/parents/${searchId}`, { pageNumber, pageSize }),
    }),

    getParentsByStatus: builder.query({
      query: ({ statusKey, pageNumber = 1, pageSize = 10, reportType = null, liaisonId = null }) =>
        queryGet(`/parents/status/${statusKey}`, { pageNumber, pageSize, reportType, adoptionLiaisonId: liaisonId }),
    }),
    getParentsByInquiryStatus: builder.query({
      query: ({ inquiryStatusKey, pageNumber = 1, pageSize = 10, reportType = null, liaisonId = null }) =>
        queryGet(`/widgets/open-inquiries/${inquiryStatusKey}`, {
          pageNumber,
          pageSize,
          reportType,
          adoptionLiaisonId: liaisonId,
        }),
    }),
    downloadParentsByStatus: builder.mutation({
      query: ({ statusKey, reportType, liaisonId }) => {
        const endpoint = `/parents/status/${statusKey}/download/excel`;
        const params = { reportType, adoptionLiaisonId: liaisonId };

        return queryPost(endpoint, {}, params);
      },
    }),

    downloadParentsByInquiryStatus: builder.mutation({
      query: ({ inquiryStatusKey, reportType, liaisonId }) => {
        const endpoint = `/widgets/open-inquiries/${inquiryStatusKey}/download/excel`;
        const params = { reportType, adoptionLiaisonId: liaisonId };

        return queryPost(endpoint, {}, params);
      },
    }),

    getParent: builder.query({
      query: ({ parentId }) => queryGet(`/parents/${parentId}`),
    }),
    saveParent: builder.mutation({
      query: ({ parentId, body }) => (parentId ? queryPut(`/parents/${parentId}`, body) : queryPost('/parents', body)),
    }),

    getParentSummary: builder.query({
      query: ({ parentId }) => queryGet(`/parents/${parentId}/summary`),
    }),
    changeParentActive: builder.mutation({
      query: ({ parentId, body }) => queryPut(`/parents/${parentId}/actions/change-active`, body),
    }),
    changeParentPlacement: builder.mutation({
      query: ({ parentId, body }) => queryPut(`/parents/${parentId}/actions/change-placementcapacity`, body),
    }),

    getParentHomeStudy: builder.query({
      query: ({ parentId }) => queryGet(`/parents/${parentId}/home-study`),
    }),
    saveParentHomeStudy: builder.mutation({
      query: ({ parentId, body }) => queryPut(`/parents/${parentId}/home-study`, body),
    }),

    getParentNotes: builder.query({
      query: ({ parentId }) => queryGet(`/parents/${parentId}/notes`),
    }),
    saveParentNote: builder.mutation({
      query: ({ parentId, id, body }) =>
        id ? queryPut(`/parents/${parentId}/notes/${id}`, body) : queryPost(`/parents/${parentId}/notes`, body),
    }),
    deleteParentNote: builder.mutation({
      query: ({ parentId, id }) => queryDelete(`/parents/${parentId}/notes/${id}`),
    }),

    getParentAccount: builder.query({
      query: ({ parentId }) => queryGet(`/parents/${parentId}/user-account`),
    }),
    getParentRecommendationV2: builder.query({
      query: ({ parentId }) => queryGet(`/parents/${parentId}/recommendations/currently-recommended-v2`),
    }),
    getParentRecommendation: builder.query({
      query: ({ parentId }) => queryGet(`/parents/${parentId}/recommendations/current`),
    }),
    saveParentRecommendation: builder.mutation({
      query: ({ parentId, id, body }) =>
        id
          ? queryPut(`/parents/${parentId}/recommendations/${id}`, body)
          : queryPost(`/parents/${parentId}/recommendations`, body),
    }),
    putParentRecommendationHold: builder.mutation({
      query: ({ parentId, body }) => queryPut(`/parents/${parentId}/recommendations/hold`, body),
    }),
    removeParentRecommendationHold: builder.mutation({
      query: ({ parentId, body }) => queryPut(`/parents/${parentId}/recommendations/remove-hold`, body),
    }),
    deleteParentRecommendation: builder.mutation({
      query: ({ parentId, body }) => queryPut(`/parents/${parentId}/recommendations`, body),
    }),
    getParentLetters: builder.query({
      query: ({ parentId, parentRecommendationHistoryId, pageNumber = 1, pageSize = 10 }) =>
        queryGet(`/parents/${parentId}/letters`, { parentRecommendationHistoryId, pageNumber, pageSize }),
    }),

    getParentFosterCare: builder.query({
      query: ({ parentId }) => queryGet(`/parents/${parentId}/foster-care/current`),
    }),
    getParentFosterCareHasLicense: builder.query({
      query: ({ parentId }) => queryGet(`/parents/${parentId}/foster-care/has-license`),
    }),
    saveParentFosterCare: builder.mutation({
      query: ({ parentId, id, body }) =>
        id
          ? queryPut(`/parents/${parentId}/foster-care/${id}`, body)
          : queryPost(`/parents/${parentId}/foster-care`, body),
    }),
    deleteParentFosterCare: builder.mutation({
      query: ({ parentId, body }) => queryPut(`/parents/${parentId}/foster-care/remove`, body),
    }),

    addParentInquiry: builder.mutation({
      query: ({ parentId, ...body }) => queryPost(`/parents/${parentId}/inquiries/general`, body),
    }),

    getParentGeneralInquiries: builder.query({
      query: ({ parentId, pageNumber = 1, pageSize = 10 }) =>
        queryGet(`/parents/${parentId}/inquiries/general`, { pageNumber, pageSize }),
    }),

    getParentChildInquiries: builder.query({
      query: ({ parentId, pageNumber = 1, pageSize = 10 }) =>
        queryGet(`/parents/${parentId}/inquiries/child`, { pageNumber, pageSize }),
    }),
    getInquiryLetters: builder.query({
      query: ({ parentId, inquiryId }) => queryGet(`/parents/${parentId}/inquiries/child/${inquiryId}/letters`),
    }),
    completeInquiry: builder.mutation({
      query: ({ parentId, inquiryId, reasonId }) =>
        queryPut(`/parents/${parentId}/inquiries/child/${inquiryId}/complete/${reasonId}`),
    }),
    getParentEvents: builder.query({
      query: ({ parentId, pageNumber = 1, pageSize = 10 }) =>
        queryGet(`/parents/${parentId}/events`, { pageNumber, pageSize }),
    }),
    getParentActivities: builder.query({
      query: ({ parentId, pageNumber = 1, pageSize = 10 }) =>
        queryGet(`/parents/${parentId}/activities`, { pageNumber, pageSize }),
    }),
    getParentPlacements: builder.query({
      query: ({ parentId, pageNumber = 1, pageSize = 10 }) =>
        queryGet(`/parents/${parentId}/placements`, { pageNumber, pageSize }),
    }),
    getParentAdoptions: builder.query({
      query: ({ parentId, pageNumber = 1, pageSize = 10 }) =>
        queryGet(`/parents/${parentId}/adoptions`, { pageNumber, pageSize }),
    }),

    getParentDocuments: builder.query({
      query: ({ parentId, pageNumber = 1, pageSize = 10 }) =>
        queryGet(`/parents/${parentId}/documents/get-all`, { pageNumber, pageSize }),
    }),
    uploadParentDocument: builder.mutation({
      query: ({ parentId, type, body }) => queryPost(`/parents/${parentId}/documents/${type}/upload`, body),
    }),
    deleteParentDocument: builder.mutation({
      query: ({ parentId, documentId }) => queryDelete(`/parents/${parentId}/documents/${documentId}`),
    }),

    getChildInquiry: builder.mutation({
      query: ({ parentId, childId, event }) => {
        const params = { event, childId, parentId };
        return queryGet(`/letters/inquiry/child`, params);
      },
    }),
    addInquiry: builder.mutation({
      query: ({ parentId, data, inquirySubType }) => {
        let url = `/parents/${parentId}/inquiries/child`;
        if (inquirySubType) {
          url = `${url}/${inquirySubType}`;
        }
        return queryPost(url, data);
      },
    }),
  }),
});

export const {
  useSearchParentsMutation,
  useQuickSearchParentMutation,
  useGetParentSearchResultsQuery,

  useGetParentsByStatusQuery,
  useGetParentsByInquiryStatusQuery,
  useDownloadParentsByStatusMutation,
  useDownloadParentsByInquiryStatusMutation,

  useGetParentQuery,
  useSaveParentMutation,

  useGetParentSummaryQuery,
  useChangeParentActiveMutation,
  useChangeParentPlacementMutation,

  useGetParentHomeStudyQuery,
  useSaveParentHomeStudyMutation,

  useGetParentNotesQuery,
  useSaveParentNoteMutation,
  useDeleteParentNoteMutation,

  useGetParentAccountQuery,
  useGetParentRecommendationV2Query,
  useGetParentRecommendationQuery,
  useSaveParentRecommendationMutation,
  usePutParentRecommendationHoldMutation,
  useRemoveParentRecommendationHoldMutation,
  useDeleteParentRecommendationMutation,
  useGetParentLettersQuery,

  useGetParentFosterCareQuery,
  useGetParentFosterCareHasLicenseQuery,
  useSaveParentFosterCareMutation,
  useDeleteParentFosterCareMutation,

  useAddParentInquiryMutation,
  useGetParentChildInquiriesQuery,
  useGetParentGeneralInquiriesQuery,
  useGetInquiryLettersQuery,
  useCompleteInquiryMutation,
  useGetParentEventsQuery,
  useGetParentActivitiesQuery,
  useGetParentPlacementsQuery,
  useGetParentAdoptionsQuery,

  useGetParentDocumentsQuery,
  useUploadParentDocumentMutation,
  useDeleteParentDocumentMutation,

  useGetChildInquiryMutation,
  useAddInquiryMutation,
} = parentApi;
